import VueCookies from 'vue-cookies';
import request from '@/helpers/request';
import templateBuilder from '@/helpers/templateBuilder';
import router from '@/router';

const getTabIdfromHash = () => router.currentRoute.hash.replace('#', '');

export default {
  state: {
    tabs: [],
    activeTabId: '',
    compatibleDevices: {},
    devicesTabTitle: '',
    devicesLoading: true,
    newOverviewTab: false,
    accessoriesLoading: true,
    accessoriesData: {},
    accessoriesByCategory: {},
    accessoryPrices: {},
    categoryNames: [],
    showSections: {},
    showNoAccessoriesText: false,
    errorAccessoriesText: false,
  },
  getters: {
    getProductTabs(state) {
      return state.tabs;
    },
    getProductTabsAreSet(state, getters) {
      return !!getters.getProductTabs.length;
    },
    getActiveProductTabs(state) {
      return state.tabs.filter((tab) => !tab.hide);
    },
    getDefaultProductTab(state, getters) {
      return getters.getActiveProductTabs[0];
    },
    getIsActiveProductTab(state, getters) {
      return (tabId) => !!getters.getActiveProductTabs.find((tab) => tab.id === tabId);
    },
    getProductTab(state) {
      return (key) => state.tabs.find((tab) => tab.id === key);
    },
    getHashIsValidProductTab(state, getters) {
      const currentTabId = getTabIdfromHash();

      return !!getters.getProductTab(currentTabId);
    },
    getProductActiveTabId(state) {
      return state.activeTabId;
    },
    getActiveProductTab(state) {
      return state.tabs.find((tab) => tab.id === state.activeTabId);
    },
    getAccessoriesData(state) {
      return state.accessoriesData;
    },
    getAccessoriesByPartNumber(state, getters) {
      return (partNumber) => getters.getAccessoriesData[partNumber];
    },
    getActiveAccessories(state, getters) {
      const partNumber = getters.getPartNumber;
      const accessories = getters.getAccessoriesByPartNumber(partNumber);
      return accessories || [];
    },
    getActiveAccessoriesByCategory(state, getters) {
      const accessories = getters.getActiveAccessories;
      return (categoryName) => accessories.filter((acc) => acc.categoryName === categoryName);
    },
    getAccessoryPrices(state) {
      return state.accessoryPrices;
    },
    getAccessoryPriceByPriceUrl(state, getters) {
      return (priceUrl) => getters.getAccessoryPrices[priceUrl];
    },
    getCompatibleDevices(state) {
      return state.compatibleDevices;
    },
    getDevicesTabTitle(state) {
      return state.devicesTabTitle;
    },
    getCompatibleDevicesByPartNum(state, getters) {
      return (partNum) => getters.getCompatibleDevices[partNum];
    },
    getActiveCompatibleDevices(state, getters) {
      const partNum = getters.getPartNumber;
      const devices = getters.getCompatibleDevicesByPartNum(partNum);
      return devices || [];
    },
    getDevicesLoading(state) {
      return state.devicesLoading;
    },
    getAccessoriesLoading(state) {
      return state.accessoriesLoading;
    },
    getNewOverviewTab(state) {
      return state.newOverviewTab;
    },
    getCategoryNames(state) {
      return state.categoryNames;
    },
    getShowSections(state) {
      return state.showSections;
    },
    getShowNoAccessoriesText(state) {
      return state.showNoAccessoriesText;
    },
    getErrorAccessoriesText(state) {
      return state.errorAccessoriesText;
    },
  },
  mutations: {
    setProductTabs(state, payload) {
      state.tabs = payload;
    },
    setShowProductTab(state, payload) {
      const { tabs, tabId } = payload;
      const targetTab = tabs.find((tab) => tabId === tab.id);
      targetTab.hide = false;

      state.tabs = tabs;
    },
    setProductActiveTabId(state, payload) {
      state.activeTabId = payload;
    },
    setCompatibleDevice(state, payload) {
      const { data, partNum } = payload;
      state.compatibleDevices = { ...state.compatibleDevices, [partNum]: data };
    },
    setDevicesLoading(state, payload) {
      state.devicesLoading = payload;
    },
    setDevicesTabTitle(state, payload) {
      state.devicesTabTitle = payload;
    },
    setAccessoriesLoading(state, payload) {
      state.accessoriesLoading = payload;
    },
    setNewOverviewTab(state, payload) {
      state.newOverviewTab = payload;
    },
    setAccessories(state, payload) {
      const { data, partNumber } = payload;
      state.accessoriesData = { ...state.accessoriesData, [partNumber]: data };
    },
    setAccessoryPrice(state, payload) {
      const { data, priceUrl } = payload;
      state.accessoryPrices = { ...state.accessoryPrices, [priceUrl]: data };
    },
    setCategoryNames(state, payload) {
      state.categoryNames = payload;
    },
    setShowSections(state, payload) {
      state.showSections = payload;
    },
    setShowNoAccessoriesText(state, payload) {
      state.showNoAccessoriesText = payload;
    },
    setErrorAccessoriesText(state, payload) {
      state.errorAccessoriesText = payload;
    },
  },
  actions: {
    setProductTabs({ dispatch }, tabs) {
      const presetTab = window.location.hash.substr(1);
      const hasPresetTab = !!presetTab;
      const tabSettings = {
        tabsPayload: tabs,
        presetTab,
        setHash: hasPresetTab,
      };

      dispatch('updateProductTabs', tabSettings);
    },
    async updateProductTabs({ commit, dispatch, getters }, tabSettings) {
      const showCompatibleDevicesTab = getters.getShowCompatibleDevicesTab;
      const devicesTabTitle = getters.getDevicesTabTitle;
      const { presetTab, tabsPayload, setHash } = tabSettings;

      commit('setNewOverviewTab', false);

      // This tab list enforces tab order
      const possibleTabs = [
        {
          id: 'overview',
          name: 'overviewTab',
        },
        {
          id: 'specs',
          name: 'specsTab',
        },
        {
          id: 'inTheBox',
          name: 'inTheBoxTab',
        },
        {
          id: 'additional',
          name: 'additionalTab',
        },
        {
          id: 'maps',
          name: 'mapsTab',
        },
        {
          id: 'requirements',
          name: 'requirementsTab',
        },
        {
          id: 'features',
          name: 'featuresTab',
        },
        {
          id: 'cycles',
          name: 'cyclesTab',
        },
        {
          id: 'coverage',
          name: 'coverageTab',
        },
        {
          id: 'accessories',
          name: 'accessoriesTab',
          content: tabsPayload.accessoriesTab !== undefined ? '<accessories />' : null,
        },
        {
          id: 'devices',
          name: 'devicesTab',
          title: devicesTabTitle,
          content: showCompatibleDevicesTab ? '<compatible-devices />' : null,
          hide: true,
        },
      ];

      const tabs = possibleTabs.reduce((tabList, tab) => {
        const { name } = tab;
        const tabData = { ...tabsPayload[name] };

        const newTab = {
          ...tabData,
          ...tab, // last one gets priority if duplicate props
        };

        // if the new tab doesn't have content, don't add it to the tab list array
        if (newTab.content) tabList.push(newTab);

        return tabList;
      }, []);

      commit('setProductTabs', tabs);
      if (showCompatibleDevicesTab) {
        await dispatch('callCompatibleDevices');
      }

      // filter out hidden tabs
      const filteredTabs = getters.getActiveProductTabs;
      if (filteredTabs && filteredTabs.length) {
        const defaultTab = filteredTabs.find((tab) => tab.id === presetTab);
        // if there isn't a hash already set, set the active tab to the first tab
        const activeTab = defaultTab ? defaultTab.id : getters.getDefaultProductTab.id;
        // if there isn't a hash already set (the user didn't select a tab),
        // don't set the hash because we'll just default to the first tab
        // We should only set the hash if the user selected a tab
        const shouldSetHash = defaultTab ? setHash : false;
        const tabPayload = {
          tabId: activeTab,
          setHash: shouldSetHash,
        };

        dispatch('setProductActiveTabId', tabPayload);
        // if the hash should not be set but the tab is being updated, clear the hash in the route
        if (!shouldSetHash) dispatch('clearRouteTabHash');
      }
    },
    setProductActiveTabId({ commit, dispatch }, payload) {
      const { tabId, setHash } = payload;
      const setRouteTabHash = setHash === undefined ? true : setHash;

      commit('setProductActiveTabId', tabId);
      if (setRouteTabHash) dispatch('setRouteTabHash', tabId);
    },
    setRouteTabHash({ dispatch }, tabId) {
      const currentHash = getTabIdfromHash();
      const newHash = `#${tabId}`;

      if (tabId !== currentHash) dispatch('setTabRoute', newHash);
    },
    clearRouteTabHash({ dispatch }) {
      const { hash } = router.currentRoute;

      if (hash) dispatch('setTabRoute', '');
    },
    setTabRoute(context, hash) {
      const { query } = router.currentRoute;

      router.push({ hash, query });
    },
    async callCompatibleDevices({ commit, dispatch, getters }) {
      const devices = getters.getCompatibleDevices;
      const partNum = getters.getPartNumber;
      const urlTemplate = getters.getDevicesJsonUrl;
      let data = devices[partNum];

      commit('setDevicesLoading', true);

      if (urlTemplate && !data) {
        const url = templateBuilder(urlTemplate, { partNum });
        data = await request({
          url,
        }).catch(() => {
          throw new Error('Unable to call compatibleDevices service');
        });

        if (!data || !data.length || !Array.isArray(data)) {
          commit('setDevicesLoading', false);
          return;
        }
      }

      const device = {
        partNum,
        data,
      };
      commit('setCompatibleDevice', device);
      commit('setDevicesLoading', false);
      dispatch('setShowProductTab', 'devices');

      // this is here so that if you load the page with #devices hash,
      // it will select it once it unhides the tab and finishes loading.
      /* eslint-disable no-restricted-globals */
      if (location.hash && location.hash === '#devices') {
        commit('setProductActiveTabId', 'devices');
      }
      /* eslint-enable no-restricted-globals */
    },
    setShowProductTab({ commit, getters }, tabId) {
      const tabs = getters.getProductTabs;
      const payload = {
        tabs,
        tabId,
      };
      commit('setShowProductTab', payload);
    },
    async callAccessories({ commit, getters }) {
      const accessoriesData = getters.getAccessoriesData;
      const activeTabId = getters.getProductActiveTabId;
      const sku = getters.getPartNumber;
      const urlTemplate = getters.getAccessoriesJsonUrl;
      const url = templateBuilder(urlTemplate, { sku });
      const accessoriesSelected = activeTabId === 'accessories';

      // if accessories tab isn't selected,
      // exit out of this call so accessories isn't called unnecessarily along with pricing
      if (!accessoriesSelected) return;

      commit('setAccessoriesLoading', true);

      let data;

      if (!urlTemplate || accessoriesData[sku]) {
        data = getters.getActiveAccessories;
      } else {
        data = await request({
          url,
        }).catch(() => {
          commit('setAccessoriesLoading', false);
          commit('setErrorAccessoriesText', true);
          throw new Error('Unable to call Accessories service');
        });
        if (!data || data.length === 0) {
          commit('setShowNoAccessoriesText', true);
        }
      }

      const categorySet = new Set();
      const showSectionsArray = {};
      const accessories = data.map(async (accessory) => {
        const accessorySku = accessory.partNumber;
        // Build the Cateogry Names set
        categorySet.add(accessory.categoryName);
        // build the showSections array (for each button assing its open/closed state)
        showSectionsArray[accessory.categoryName] = false;
        // Add the link to the accessory
        accessory.link = templateBuilder(getters.getSeoUrl, { seoUrl: accessory.seoUrl });

        // Add the Add to Cart link
        const addToCartUrl = getters.getAddToCartUrl;
        const addToCartUrlOld = getters.getAddToCartUrlOld;
        accessory.addToCartLink = templateBuilder(addToCartUrl, {
          partNum: accessorySku,
          quantity: '1',
        });
        accessory.addToCartUrlOld = templateBuilder(addToCartUrlOld, {
          partNum: accessorySku,
          quantity: '1',
        });

        return accessory;
      });

      // Sets do not work well with IE, reconverting to array
      const categoryNames = [...categorySet];

      commit('setCategoryNames', categoryNames);
      commit('setShowSections', showSectionsArray);

      Promise.all(accessories).then((response) => {
        const cacheValue = {
          data: response,
          partNumber: sku,
        };
        commit('setAccessories', cacheValue);
        commit('setAccessoriesLoading', false);
      });
    },
    async setAccessoriesPrices({ commit, dispatch, getters }, category) {
      const categoryAccessories = getters.getActiveAccessoriesByCategory(category);
      const partNumber = getters.getPartNumber;

      // Set the prices for accessories matching the category
      const pricedAccessories = await Promise.all(
        categoryAccessories.map(async (accessory) => {
          const newAccessory = await dispatch('setAccessoryPrice', accessory);
          return newAccessory;
        }),
      );

      // Get current active Accessories
      const activeAccessories = getters.getActiveAccessories;
      // if no accessories were priced, return original accessories
      if (!pricedAccessories.length) return activeAccessories;

      // Replace the accessories in the active accessories with the newly priced accessories
      const newAccessories = activeAccessories.reduce((newAccs, acc) => {
        const pricedAcc = pricedAccessories.find((priced) => priced.partNumber === acc.partNumber);
        const newAcc = pricedAcc || acc;

        newAccs.push(newAcc);

        return newAccs;
      }, []);

      const payload = {
        data: newAccessories,
        partNumber,
      };

      await commit('setAccessories', payload);
      return newAccessories;
    },
    async setAccessoryPrice({ getters, commit }, accessory) {
      const newAccessory = { ...accessory };
      const { partNumber } = newAccessory;

      // Cookie accessible only on garmin domain
      const cookie = VueCookies.get('GarminBuyCacheKey');
      const customerGroup = cookie || 'none';

      // Set price url
      const { getBuyGarminEndpoint, getLocale, getStoreCode } = getters;
      const priceUrlTemplate = `${getBuyGarminEndpoint}/${getLocale}/${getStoreCode}/price/sku/{{sku}}.json?cg={{customerGroup}}`;
      const priceUrl = templateBuilder(priceUrlTemplate, { sku: partNumber, customerGroup });

      // If accessory price has already been called and cached, return null
      // returning null indicates the price was not called
      const cachedPrice = getters.getAccessoryPriceByPriceUrl(priceUrl);

      // if the price call is already cached, return that, else call to get price
      const priceData = cachedPrice || await request({
        url: priceUrl,
      }).catch(() => {
        // ignore error on price call.
        // 404 on price call means there isn't a price to display
      });

      const priceObj = priceData || {};
      // set that price api has been called and has finished loading
      priceObj.priceLoaded = true;

      const newPrice = {
        data: priceObj,
        priceUrl,
      };

      // Add the price to the accessory
      newAccessory.priceObj = priceObj;

      // Cache accessory price
      commit('setAccessoryPrice', newPrice);

      return newAccessory;
    },
    setAllSections({ dispatch, getters }, showState) {
      const categoryNames = getters.getCategoryNames;

      const payload = {
        categories: categoryNames,
        showState,
      };

      dispatch('setSections', payload);
    },
    setSection({ dispatch, getters }, category) {
      const data = { ...getters.getShowSections };
      const payload = {
        categories: [category],
        showState: !data[category],
      };

      dispatch('setSections', payload);
    },
    setSections({ commit, dispatch, getters }, payload) {
      const { categories, showState } = payload;
      const data = { ...getters.getShowSections };

      categories.forEach((category) => {
        data[category] = showState;
        if (showState) dispatch('setAccessoriesPrices', category);
      });

      commit('setShowSections', data);
    },
    setNewOverviewTab({ commit }, payload) {
      commit('setNewOverviewTab', payload);
    },
    setDevicesTabTitle({ commit }, payload) {
      commit('setDevicesTabTitle', payload);
    },
  },
};
